import { inject, Injectable } from '@angular/core';
import { ConfigSettingsService } from '@ebeihilfe/core';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, shareReplay, switchMap } from 'rxjs';

import { AntragLimit } from '../model';
import { EinreichungApiService } from './einreichung-api.service';

/**
 * Fallback limit used when the limit request fails: treated as "not limited" so a transient API error
 * does not break the UI or block the user. The actual daily limit is additionally enforced server-side
 * on submission, so failing open here cannot let a user exceed the configured limit.
 */
const UNKNOWN_LIMIT = new AntragLimit({ antraegeLeftToday: 0, isLimited: false, limit: 0 });

@Injectable({
  providedIn: 'root',
})
export class AntragsLimitService {
  private readonly einreichungApiService = inject(EinreichungApiService);
  private readonly configService = inject(ConfigSettingsService);

  private readonly reload$ = new BehaviorSubject<void>(undefined);

  /**
   * Hot, cached stream of the current limit. It re-emits whenever {@link fetchAntragsLimit} is called
   * (e.g. after a submission), which is why it intentionally never completes – completing it (e.g. via
   * `take(1)`) would stop `async`-pipe bindings from receiving those updates.
   *
   * A failed limit request is caught per emission and mapped to {@link UNKNOWN_LIMIT}, so a transient
   * error neither tears down the (shared, replayed) stream nor rejects snapshot reads; a later
   * {@link fetchAntragsLimit} simply re-runs the request and recovers.
   *
   * The public API deliberately separates the two consumption modes: the `…$` accessors expose this
   * live stream for `async`-pipe bindings, while the `…Now()` methods return a one-shot snapshot
   * `Promise` for event handlers – callers never `firstValueFrom` this never-completing stream directly.
   */
  private readonly antragsLimit$ = this.reload$.pipe(
    switchMap(() => this.einreichungApiService.getAntragsLimit().pipe(catchError(() => of(UNKNOWN_LIMIT)))),
    shareReplay(1),
  );

  public fetchAntragsLimit(): void {
    this.reload$.next();
  }

  /** Live limit stream for `async`-pipe bindings. Never completes – do not `await` it expecting completion. */
  public getAntragsLimit(): Observable<AntragLimit> {
    return this.antragsLimit$;
  }

  /** Live "limit reached" stream for `async`-pipe bindings. */
  public isLimitReached$(): Observable<boolean> {
    return this.antragsLimit$.pipe(map((antragLimit) => antragLimit.isLimited && antragLimit.antraegeLeftToday === 0));
  }

  /**
   * Live "start blocked" stream for `async`-pipe bindings. Starting a new Antrag is blocked only when the
   * daily limit is reached AND drafts cannot be saved – if drafts can be saved the user may still run
   * through the assistant and keep a draft (only the actual submission is limited).
   */
  public isStartBlockedByLimit$(): Observable<boolean> {
    const canSaveDrafts = this.configService.appSettings.customizing.hasUnfinishedAntrage;

    return this.isLimitReached$().pipe(map((reached) => reached && !canSaveDrafts));
  }

  /** One-shot snapshot read of {@link isLimitReached$} – safe to `await` in event handlers. */
  public isLimitReachedNow(): Promise<boolean> {
    return firstValueFrom(this.isLimitReached$());
  }

  /** One-shot snapshot read of {@link isStartBlockedByLimit$} – safe to `await` in event handlers. */
  public isStartBlockedNow(): Promise<boolean> {
    return firstValueFrom(this.isStartBlockedByLimit$());
  }
}
